import { ItemShippingSelect } from '@c/checkout/Shipping';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import { ChevronLeftIcon, FedExIcon, PlusIcon, USPSIcon } from '@c/icons';
import { UPS } from '@c/icons/carriers';
import { BProcess, ProcessStep } from '@c/process/BaseProcess';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStripe } from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import SafeImage from '@ui/SafeImage';
import Spinner from '@ui/Spinner';
import { BidDocument, BidSchema } from '@util/firestore/bid';
import {
  createBid,
  createBidAndUpdateOffer,
} from '@util/firestore/bid/bid.service';
import {
  createOffer,
  createOfferIntent,
  getOfferId,
  getOfferMessage,
} from '@util/firestore/offers';
import { getPaymentMethods } from '@util/firestore/payments/payments.service';
import { getProductById } from '@util/firestore/products/products.service';
import { getTax } from '@util/firestore/taxjar';
import { getUserById } from '@util/firestore/users';
import { capitalize, formatCurrency, getHostUrl, isMobile } from '@util/index';
import { logError } from '@util/logError';
import { getAttribution } from '@util/marketing/attribution';
import {
  AddressDocument,
  addressDocumentSchema,
} from '@util/types/firestore/address';
import { OfferDocument } from '@util/types/firestore/offers';
import { ProductDocument } from '@util/types/firestore/products';
import { useAuth } from 'context/AuthContext';
import { useEffectivePrice } from 'context/StoreSaleContext';
import { getOfferPriceCeiling } from '@util/storeSale/effectivePrice';
import {
  calculateShipping,
  useShoppingCart,
} from 'context/ShoppingCartContext';
import Link from 'next/link';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import AddAddressModal from './AddAddressModal';
import AddPaymentMethodModal from './AddPaymentMethodModal';
import BaseModal from './BaseModal';

const OfferShippingOptions = ({
  product,
  shipTo,
  setShippingCost,
}: {
  product: ProductDocument;
  shipTo: AddressDocument;
  setShippingCost: (cost: number) => void;
}) => {
  const [showShippingOptions, setShowShippingOptions] = useState(false);
  const { selectedShippingRates } = useShoppingCart();
  const selectedRate = selectedShippingRates
    .get(product.seller_id)
    ?.get(product.id);

  setShippingCost(selectedRate?.total_amount ?? 0);
  return (
    <div className="flex flex-col gap-[1.6rem]">
      <span className="font-semibold">Selected Shipping</span>
      {selectedRate && (
        <>
          <div className="flex w-full gap-[1.6rem]">
            {selectedRate.carrier_code === 'fedex' ? (
              <FedExIcon />
            ) : selectedRate.carrier_code === 'ups' ? (
              <UPS />
            ) : (
              <USPSIcon />
            )}
            <div className="flex grow items-center gap-5">
              <div>
                <h5 className="text-[1.6rem] font-semibold text-brand-secondary">
                  {selectedRate.delivery_days} Days
                </h5>
                <p className="text-[1.4rem] text-brand-gray">
                  {selectedRate.service_type}
                </p>
              </div>
              <div className="ml-auto text-[1.6rem] font-semibold">
                {formatCurrency(selectedRate.total_amount)}
              </div>
            </div>
          </div>
          <Button
            type="text"
            onClick={() => setShowShippingOptions((s) => !s)}
            text={
              showShippingOptions
                ? 'Hide Shipping Options'
                : 'Show More Shipping Options'
            }
          />
        </>
      )}
      {/* When no rate is selected yet, render the rate picker directly so the
          buyer sees loading, then auto-selected carrier rates, or a visible
          error + Retry — instead of a skeleton that spins forever if the rate
          fetch fails. useApiRoute routes through the live Next.js API route
          (the old getRates Cloud Function was deleted). GB #1313. */}
      <div className="max-h-[35rem] overflow-y-auto">
        <ItemShippingSelect
          chooseCheapestByDefault
          hideProduct
          showRemoveOption={false}
          useApiRoute
          product={product}
          shipTo={shipTo}
          hide={selectedRate ? !showShippingOptions : false}
        />
      </div>
    </div>
  );
};
interface MakeAnOfferModalProps {
  isOpen: boolean;
  dismiss: (openDrawer?: boolean) => void;
  product: ProductDocument;
  variant?: 'bid' | 'offer';
  highestBid?: BidDocument;
  isEditingBid?: boolean;
  currentUserBid?: BidDocument;
  bidInput?: string;
}

const OfferFormSchema = z.object({
  price: z.string().min(1),
  maxPrice: z.string().optional(),
  message: z.string().optional(),
  shippingAddress: z.string().min(1),
  paymentMethodId: z.string().min(1),
  termsAndConditions: z.boolean().refine((v) => v === true, {
    message: 'You must agree to the terms and conditions',
  }),
});

type OfferSchemaType = z.infer<typeof OfferFormSchema>;

// These survive a modal unmount: closing/reopening must not start another send.
const offerSubmissionsInFlight = new Set<string>();
const offerSubmissionListeners = new Set<() => void>();
const subscribeToOfferSubmissions = (onChange: () => void) => {
  offerSubmissionListeners.add(onChange);
  return () => {
    offerSubmissionListeners.delete(onChange);
  };
};

const MakeAnOfferModal = ({
  isOpen,
  dismiss,
  product,
  variant = 'offer',
  highestBid,
  isEditingBid,
  currentUserBid,
  bidInput,
}: MakeAnOfferModalProps) => {
  const { userDoc } = useAuth();
  const storePrice = useEffectivePrice(product);
  const askingPrice = storePrice?.applied ? storePrice.unitCents / 100 : product.price;
  const offerCeiling = getOfferPriceCeiling(askingPrice, product.minimum_offer_price ?? 0);
  const defaultMaxBid =
    (currentUserBid?.max_bid ?? 0) > (highestBid?.bid_price ?? 0)
      ? currentUserBid?.max_bid?.toString()
      : '';
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<OfferSchemaType>({
    resolver: zodResolver(OfferFormSchema),
    defaultValues: {
      price: bidInput,
      maxPrice: defaultMaxBid,
    },
  });

  const [openModal, setOpenModal] = useState<'address' | 'payment' | ''>('');
  const submissionKey = `${userDoc?.uid}:${product.id}:${variant}`;
  const loadingSubmit = useSyncExternalStore(
    subscribeToOfferSubmissions,
    () => offerSubmissionsInFlight.has(submissionKey),
    () => false
  );
  const [loadingRates, setLoadingRates] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { selectedShippingRates } = useShoppingCart();
  const selectedRate = selectedShippingRates
    .get(product.seller_id)
    ?.get(product.id);

  const [shippingCost, setShippingCost] = useState(
    !product.is_flat_rate ? selectedRate?.total_amount ?? 0 : 0
  );

  const { data: paymentMethodData, isLoading, error: paymentMethodsError } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: ({ signal }) => getPaymentMethods(signal),
    enabled: !!userDoc,
    retry: false, // Don't retry auth failures
  });

  // Check if the error is an auth expiration
  const errorMessage = (paymentMethodsError as Error)?.message ?? '';
  const isAuthExpired = errorMessage.includes('authenticated') ||
    errorMessage.includes('unauthenticated');

  const addressOptions =
    userDoc?.addresses?.map((address) => ({
      id: `${address.name} - ${address.address_line1}`,
      label: `${address.name} - ${address.address_line1}`,
      value: JSON.stringify(address),
    })) ?? [];

  useEffect(() => {
    if (userDoc?.addresses?.length) {
      const addy =
        userDoc.addresses.find((a) => a.is_default) ?? userDoc.addresses[0];
      setValue('shippingAddress', JSON.stringify(addy));
      const shipping = calculateShipping(product, addy);
      setShippingCost(shipping?.cost ?? 0);
    }
    if (paymentMethodData?.length) {
      const mostRecent = paymentMethodData[paymentMethodData.length - 1];
      setValue('paymentMethodId', mostRecent.id!);
    }
  }, [userDoc, paymentMethodData, product, setValue]);

  const getNumber = (numStr: string) =>
    numStr ? Number(numStr.replace(/,/g, '')) : 0;

  const paymentOptions = paymentMethodData?.map((paymentMethod) => ({
    id: paymentMethod.id ?? '',
    label: `${capitalize(paymentMethod.brand)} - ${paymentMethod.last4}`,
    value: paymentMethod.id ?? '',
  }));

  const form = watch();

  const stripe = useStripe();
  const updateShipping = async () => {
    setLoadingRates(true);
    if (!form.shippingAddress) {
      setError(
        'shippingAddress',
        {
          type: 'manual',
          message: `No shipping address selected`,
        },
        { shouldFocus: true }
      );
      setLoadingRates(false);
      return;
    }
    const shippingAddress = JSON.parse(form.shippingAddress);

    const shipping = calculateShipping(product, shippingAddress);
    if (product.is_flat_rate && !shipping) {
      setError(
        'shippingAddress',
        {
          type: 'manual',
          message: `No shipping options available for this product to this address`,
        },
        { shouldFocus: true }
      );
      setLoadingRates(false);
      return;
    }
    if (shipping) {
      setShippingCost(shipping.cost);
    }

    setLoadingRates(false);
    setCurrentStep(2);
  };

  const updateBid = () => {
    const bidPrice = getNumber(form.price);
    let maxPrice: number | undefined;
    if (form.maxPrice) {
      maxPrice = getNumber(form.maxPrice);
    }
    if (highestBid && getNumber(form.price) <= highestBid?.bid_price) {
      return;
    }
    const offer_id = currentUserBid?.offer_id;

    const newBid = BidSchema.parse({
      product_id: product.id,
      username: userDoc?.username,
      bid_price: getNumber(form.price),
      ...(maxPrice &&
        maxPrice > bidPrice && {
          max_bid: maxPrice,
        }),
      uid: userDoc?.uid,
      offer_id,
      created: Date.now(),
      auto: false,
    });
    createBidAndUpdateOffer(newBid).then(() => {
      dismiss();
    });
  };

  const onSubmit = async (data: OfferSchemaType) => {
    if (!userDoc || !stripe) return;
    // Acquire synchronously, before any await, across all mounts for this buyer/item.
    if (offerSubmissionsInFlight.has(submissionKey)) return;
    offerSubmissionsInFlight.add(submissionKey);
    offerSubmissionListeners.forEach((onChange) => onChange());
    try {
      setSubmitError('');

      // Re-verify product availability (handles stale browser tabs)
      const freshProduct = await getProductById(product.id);
      if (!freshProduct || freshProduct.out_of_stock || (freshProduct.stock ?? 1) < 1) {
        setSubmitError('This item has sold and is no longer available.');
        return;
      }

      if (!form.termsAndConditions) {
        setError(
          'termsAndConditions',
          {
            type: 'manual',
            message: `You must agree to the terms and conditions`,
          },
          { shouldFocus: true }
        );
      }

      const priceNumber = getNumber(data.price);
      if (variant === 'offer' && priceNumber < product.minimum_offer_price) {
        setError('price', {
          type: 'manual',
          message: `Your offer is lower than the buyers minimum offer price. Please try again.`,
        });
        setCurrentStep(0);
        return;
      }
      if (variant === 'bid' && priceNumber < (highestBid?.bid_price ?? 0)) {
        setError('price', {
          type: 'manual',
          message: `Your bid is lower than current bid. Please try again.`,
        });
        setCurrentStep(0);
      }
      if (variant === 'offer' && priceNumber > offerCeiling) {
        setError('price', {
          type: 'manual',
          message: `Your offer is higher than the buyers asking price. Did you mean to buy now?`,
        });
        setCurrentStep(0);
        return;
      }
      const shippingAddressObj = JSON.parse(data.shippingAddress);
      shippingAddressObj.address_line2 = shippingAddressObj.address_line2 ?? '';
      const shippingAddress = addressDocumentSchema.parse(shippingAddressObj);
      if (product.is_flat_rate) {
        const shipping = calculateShipping(product, shippingAddress);
        if (!shipping) throw new Error('Shipping Unavailable');
        setShippingCost(shipping.cost);
      }
      const paymentMethod = paymentMethodData?.find(
        (paymentMethod) => paymentMethod.id === data.paymentMethodId
      );
      if (!paymentMethod?.id) throw new Error('Please select a payment method');
      const seller = await getUserById(product.seller_id);
      if (!seller) throw new Error('Seller not found');

      const now = Date.now();

      const offer: OfferDocument = {
        id: getOfferId(),
        payment_id: paymentMethod.id,
        buyer_id: userDoc.uid,
        seller_id: seller.uid,
        rate_id: selectedRate?.rate_id ?? '',
        product_id: product.id,
        current_price: data.price ? getNumber(data.price) : 0,
        account_id: seller.account_id ?? '',
        customer_id: userDoc.customer_id,
        is_auction: variant === 'bid',
        ...(!!product.end_time && {
          end_time: product.end_time,
        }),
        ...(!!data.maxPrice && { max_bid: getNumber(data.maxPrice) }),
        price: getNumber(data.price),
        shipping_cost: shippingCost,
        total: shippingCost + getNumber(data.price) + tax,
        message: data.message ?? '',
        address: shippingAddress,
        payment: paymentMethod,
        uids: [userDoc.uid, seller.uid],
        created: now,
        time_remaining:
          variant === 'offer' ? now + 172_800_000 : product.end_time!, //add 48 hours
        from_web: true,
        state: 0,
        tax,
        // Stage 2: Capture buyer email and attribution at offer creation
        buyer_email: userDoc.email,
        attribution: getAttribution() ?? undefined,
      };
      if (variant === 'offer') {
        offer.message = getOfferMessage(
          product.title,
          offer.price!,
          offer.shipping_cost!,
          offer.tax!
        );
      }

      if (!offer.price) {
        setError('price', {
          type: 'manual',
          message: `Please enter a valid price`,
        });
        setCurrentStep(0);
        return;
      }

      let offerIntent;
      try {
        offerIntent = await createOfferIntent(offer.price, offer.customer_id);
      } catch (err: any) {
        // Check if auth expired during submission
        if (err?.message?.includes('authenticated') || err?.message?.includes('unauthenticated')) {
          setSubmitError('Your session has expired. Please refresh the page and sign in again.');
        } else {
          setSubmitError(err?.message ?? 'Error creating offer. Please try again.');
        }
        return;
      }

      const return_url = `${getHostUrl()}/dashboard/offers?retry=true`;
      const { error } = await stripe.confirmCardSetup(offerIntent.data, {
        payment_method: paymentMethod.id,
        return_url,
      });
      if (error) {
        // Make connection errors more user-friendly with retry suggestion
        const isConnectionError = error.message?.toLowerCase().includes('connection') ||
          error.message?.toLowerCase().includes('network') ||
          error.type === 'api_connection_error';

        setSubmitError(
          isConnectionError
            ? 'Connection issue with payment provider. Please try again.'
            : (error.message ?? 'Error submitting offer. Please try again or contact support')
        );
        return;
      }
      await createOffer(offer, data.message);
      if (variant === 'bid') {
        const bid = BidSchema.parse({
          uid: userDoc.uid,
          username: userDoc.username,
          offer_id: offer.id,
          product_id: product.id,
          bid_price: offer.price,
          ...(!!offer.max_bid && { max_bid: offer.max_bid }),
        });
        await createBid(bid);
      }
      dismiss(true);
    } catch (error) {
      logError(error);
      setSubmitError('Error submitting offer. Please try again or contact support');
    } finally {
      offerSubmissionsInFlight.delete(submissionKey);
      offerSubmissionListeners.forEach((onChange) => onChange());
    }
  };

  const [currentStep, setCurrentStep] = useState(0);

  const reviewShipping = () => {
    // if price and terms and conditions are valid, go to review step
    if (form.price) {
      const priceNumber = getNumber(form.price);
      const maxBid = getNumber(form.maxPrice ?? '0');
      if (variant === 'offer' && priceNumber < product.minimum_offer_price) {
        setError('price', {
          type: 'manual',
          message: `Your offer is lower than the buyers minimum offer price. Please try again.`,
        });
        return;
      }

      if (variant === 'bid' && priceNumber < product.price + 1) {
        setError('price', {
          type: 'manual',
          message: `Your bid must be at least $1 higher than the current bid. Please try again.`,
        });
        setCurrentStep(0);
        return;
      }
      if (variant === 'bid' && maxBid && maxBid < priceNumber + 1) {
        setError('maxPrice', {
          type: 'manual',
          message: `Your max bid must be at least $1 higher than your current bid. Please try again.`,
        });
        setCurrentStep(0);
        return;
      }
      if (variant === 'offer' && priceNumber > offerCeiling) {
        setError('price', {
          type: 'manual',
          message: `Your offer is higher than the buyers asking price. Did you mean to buy now?`,
        });
        return;
      }
      clearErrors('price');
      setCurrentStep(1);
    } else {
      // else, show errors
      if (!form.price) {
        setError('price', {
          type: 'manual',
          message: `Please enter an ${variant} price`,
        });
      }
    }
  };

  const [tax, setTax] = useState(0);
  useEffect(() => {
    const total = getNumber(form.price) + shippingCost;
    if (!form.shippingAddress || !product || !total) return;
    const shippingAddressObj = JSON.parse(form.shippingAddress);
    shippingAddressObj.address_line2 = shippingAddressObj.address_line2 ?? '';
    const shippingAddress = addressDocumentSchema.parse(shippingAddressObj);

    getTax({
      product_id: product.id,
      seller_id: product.seller_id,
      to_address: shippingAddress!,
      total,
      shipping: shippingCost,
    }).then((tax) => {
      setTax(tax.amount);
    });
  }, [form.price, form.shippingAddress, product, shippingCost]);

  const steps: ProcessStep<any>[] = [
    {
      id: 'agreement',
      content: (
        <div className="flex w-full max-w-[40rem] flex-col gap-[2.4rem] px-1">
          {variant === 'bid' ? (
            <>
              <span className="w-full text-brand-dark-gray">
                If you have the highest bid once the auction expires, you will
                be automatically charged your bid price.{' '}
                <Link
                  className="text-brand-secondary"
                  href="/help"
                  target="_blank"
                >
                  Learn more
                </Link>
              </span>
              <div className="rounded-[1rem] bg-[#E9F4E9] p-6">
                <div className="pb-1 pt-2 text-xl font-light text-neutral-800">
                  Current Highest Bid
                </div>
                <div className="text-[2.4rem] font-medium text-green-700">
                  {formatCurrency(highestBid?.bid_price ?? product.price)}
                </div>
              </div>
            </>
          ) : (
            <>
              <span className="w-full text-brand-dark-gray">
                Set your price and see if the seller bites. If they accept,
                we&apos;ll process your payment automatically and get your gear on the way.
              </span>
              <div className="flex items-center gap-[1.6rem]">
                <SafeImage
                  className="h-[6rem] w-[6rem] shrink-0 rounded-2xl"
                  alt={product.title}
                  src={product.thumbnail}
                />
                <div className="flex flex-col gap-[0.2rem] text-brand-lightest-black">
                  <span className="line-clamp-2">{product.title}</span>
                  <span className="text-[1.8rem] font-bold text-brand-dark-gray">
                    {storePrice?.applied ? 'Buy Now' : 'Price'}: {formatCurrency(askingPrice)}
                  </span>
                </div>
              </div>
            </>
          )}
          <div className="flex flex-col items-start gap-[1.6rem]">
            <Controller
              control={control}
              name={`price`}
              render={({ field: { onChange, onBlur } }) => (
                <FormLabel
                  required
                  labelClasses="font-medium"
                  value={variant === 'offer' ? 'Offer price' : 'Your bid'}
                >
                  <FormCurrencyInputFancy
                    full
                    placeholder={'0.00'}
                    error={!!errors.price}
                    onChange={(e) => {
                      clearErrors('price');
                      onChange(e);
                    }}
                    defaultValue={form.price}
                    onBlur={onBlur}
                  />
                </FormLabel>
              )}
            />
            {errors.price && (
              <span className="text-[1.2rem] text-brand-red">
                {errors.price.message}
              </span>
            )}
            {variant === 'offer' && (
              <FormLabel value="Message to seller (Optional)">
                <FormInput
                  placeholder="Give message to seller"
                  error={!!errors.message}
                  {...register('message')}
                />
              </FormLabel>
            )}
            {variant === 'bid' && (
              <>
                <Controller
                  control={control}
                  name={`maxPrice`}
                  render={({ field: { onChange, onBlur } }) => (
                    <FormLabel
                      labelClasses="font-medium"
                      value="Max Bid (Optional)"
                    >
                      <FormCurrencyInputFancy
                        full
                        placeholder="0.00"
                        error={!!errors.maxPrice}
                        onChange={(e) => {
                          clearErrors('maxPrice');
                          onChange(e);
                        }}
                        onBlur={onBlur}
                        defaultValue={form.maxPrice}
                      />
                    </FormLabel>
                  )}
                />
                {errors.maxPrice && (
                  <span className="text-[1.2rem] text-brand-red">
                    {errors.maxPrice.message}
                  </span>
                )}
                <span className="-mt-4 mb-3 text-[1rem] text-brand-gray">
                  We will automatically place your bids until the price reaches
                  your max bid.
                </span>
              </>
            )}
          </div>
          <div className="flex w-full items-center justify-center gap-[1.6rem] pb-[2.4rem]">
            <div className="font-semibold text-brand-secondary">
              <Button
                type="text"
                text="Cancel"
                disabled={loadingSubmit}
                width="small"
                onClick={() => dismiss()}
              />
            </div>
            <Button
              type="secondary"
              text={variant === 'bid' ? 'Place bid' : 'Make offer'}
              buttonType="button"
              width="normal"
              onClick={() => {
                if (isEditingBid) {
                  updateBid();
                } else {
                  reviewShipping();
                }
              }}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'shipping-and-payment',
      title: 'Shipping & Payment',
      content: (
        <div className="flex min-h-[42rem] w-full max-w-[40rem] flex-col justify-between gap-[2.4rem] px-[2.4rem]">
          <div className="flex flex-col gap-[1.6rem]">
            <div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                  <SafeImage
                    className="shrink-0 rounded-2xl"
                    src={product.thumbnail}
                    width={44}
                    height={44}
                    alt={''}
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className="text-h4 line-clamp-2 leading-8">
                      {product.title}
                    </h4>
                    <span className="text-lg text-brand-gray">
                      {product.categories[0]}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col text-right">
                  <span className="text-sm text-brand-gray">{variant === 'offer' && storePrice?.applied ? 'Buy Now price' : 'Listed Price'}</span>
                  <span className="font-semibold">
                    {formatCurrency(askingPrice)}
                  </span>
                  {variant === 'offer' && (
                    <>
                      <span className="mt-1 text-sm text-brand-gray">Your Offer</span>
                      <span className="font-semibold text-brand-orange">
                        {formatCurrency(getNumber(form.price))}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-end gap-[1.2rem]">
              <div className="min-w-0 flex-1">
                <FormLabel
                  value="Shipping address"
                  required
                  errorMessage={errors.shippingAddress?.message}
                >
                  <FormSelect
                    disabled={!addressOptions.length}
                    allowEmpty={false}
                    placeholder="Select shipping address"
                    error={!!errors.shippingAddress}
                    options={addressOptions}
                    rules={{ required: true }}
                    control={control}
                    name="shippingAddress"
                  />
                </FormLabel>
              </div>
              <div className="flex shrink-0 items-end self-end pb-[0.4rem]">
                <Button
                  width="small"
                  height="small"
                  buttonType="button"
                  type="tertiary"
                  leadingIcon={<PlusIcon />}
                  onClick={() => setOpenModal('address')}
                />
                <AddAddressModal
                  isOpen={openModal === 'address'}
                  dismiss={(address) => {
                    setOpenModal('');
                    if (address && product.is_flat_rate) {
                      setValue('shippingAddress', JSON.stringify(address));
                      const shipping = calculateShipping(product, address);
                      setShippingCost(shipping?.cost ?? 0);
                    }
                  }}
                />
              </div>
            </div>
            <div className="flex items-end gap-[1.2rem]">
              <div className="min-w-0 flex-1">
                {!isLoading ? (
                  <FormLabel value="Payment method" required>
                    <FormSelect
                      disabled={!paymentOptions?.length}
                      allowEmpty={false}
                      placeholder="Select payment method"
                      error={!!errors.paymentMethodId}
                      options={paymentOptions ?? []}
                      control={control}
                      name="paymentMethodId"
                    />
                  </FormLabel>
                ) : (
                  //  animate pulse in the shape of a rounded select input
                  <div className="flex h-[4.8rem] w-full animate-pulse items-center justify-center rounded-full bg-gray-200">
                    Loading payment methods...
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-end self-end pb-[0.4rem]">
                <Button
                  width="small"
                  height="small"
                  buttonType="button"
                  type="tertiary"
                  leadingIcon={<PlusIcon />}
                  onClick={() => setOpenModal('payment')}
                />
                <AddPaymentMethodModal
                  isOpen={openModal === 'payment'}
                  dismiss={(paymentMethodId) => {
                    setOpenModal('');
                    if (paymentMethodId)
                      setValue('paymentMethodId', paymentMethodId);
                  }}
                />
              </div>
            </div>
            {/* {isEditingBid && (
              <div className="flex flex-col">
                <Controller
                  control={control}
                  name={`maxPrice`}
                  render={({ field: { onChange, onBlur } }) => (
                    <FormLabel value="Max bid (optional)">
                      <FormCurrencyInputFancy
                        full
                        placeholder="0.00"
                        error={!!errors.price}
                        onChange={(e) => {
                          clearErrors('price');
                          onChange(e);
                        }}
                        defaultValue={currentUserBid?.max_bid?.toString() ?? ''}
                        onBlur={onBlur}
                      />
                    </FormLabel>
                  )}
                />
                <span className="mt-4 text-[1.3rem] text-brand-lighter-black">
                  Leave empty to remove max bid.
                </span>
              </div>
            )} */}
          </div>
          {form.shippingAddress && !product.is_flat_rate && (
            <OfferShippingOptions
              product={product}
              shipTo={addressDocumentSchema.parse(
                JSON.parse(form.shippingAddress)
              )}
              setShippingCost={setShippingCost}
            />
          )}

          {variant === 'offer' && (
            <p className="text-center text-[1.3rem] text-brand-gray">
              We only charge your card when the seller accepts your offer. And
              watch your inbox—counteroffers come through fast, and we&apos;ll
              alert you as soon as one hits!
            </p>
          )}

          <div className="flex justify-center gap-[1.6rem]">
            <div className="font-semibold text-brand-secondary">
              <Button
                buttonType="button"
                type="text"
                text="Cancel"
                disabled={loadingSubmit}
                width="small"
                onClick={() => dismiss()}
              />
            </div>
            <Button
              type="secondary"
              text={product.is_flat_rate ? `Review ${variant}` : 'Continue'}
              disabled={
                !form.shippingAddress ||
                !form.paymentMethodId ||
                (!product.is_flat_rate && !selectedRate)
              }
              width="normal"
              loading={loadingRates || loadingSubmit}
              onClick={() => {
                updateShipping();
              }}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'confirm',
      title: `Review ${variant === 'offer' ? 'Offer' : 'Bid'}`,
      content: (
        <div className="flex min-h-[42rem] w-full max-w-[40rem] flex-col justify-between gap-[2.4rem] px-[2.4rem]">
          <div className="flex flex-col gap-[0.8rem]">
            {submitError && (
              // error message
              <span className="text-[1.3rem] font-semibold text-red-500">
                {submitError}
              </span>
            )}
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 flex-1 items-center gap-4">
                <SafeImage
                  className="shrink-0 rounded-2xl"
                  src={product.thumbnail}
                  width={44}
                  height={44}
                  alt={''}
                />
                <div className="min-w-0 flex-1">
                  <h4 className="text-h4 line-clamp-2 leading-8">
                    {product.title}
                  </h4>
                  <span className="text-lg text-brand-gray">
                    {product.categories[0]}
                  </span>
                </div>
              </div>
              <span className="shrink-0 font-semibold">
                {formatCurrency(askingPrice)}
              </span>
            </div>
            <hr className="mb-4 mt-5"></hr>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between text-lg text-brand-gray">
                <span>{variant === 'offer' ? 'Your Offer' : 'Your Bid'}</span>
                <span className="shrink-0 font-semibold text-brand-orange">
                  {formatCurrency(getNumber(form.price))}
                </span>
              </div>
              <div className="flex items-center justify-between text-lg text-brand-gray">
                <span>Shipping</span>
                <span className="shrink-0">{formatCurrency(shippingCost)}</span>
              </div>
              <div className="flex items-center justify-between text-lg text-brand-gray">
                <span>Tax</span>
                <span className="shrink-0">{formatCurrency(tax)}</span>
              </div>
              <div className="flex items-center justify-between font-semibold">
                <span>Total (USD)</span>
                <span className="shrink-0">
                  {formatCurrency(shippingCost + getNumber(form.price) + tax)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-[1.6rem]">
            {variant === 'offer' && (
              <p className="text-[1.3rem] text-brand-dark-gray">
                Everything looks good? Once the seller accepts, your offer becomes a
                purchase and we&apos;ll process your payment. Until then, nothing is charged.
              </p>
            )}
            <Checkbox
              label={
                variant === 'offer'
                  ? 'I understand that an accepted offer becomes a purchase'
                  : `I acknowledge that this bid is binding`
              }
              labelClassName="text-[1.3rem] font-semibold"
              selected={form.termsAndConditions}
              error={!!errors.termsAndConditions}
              onChange={() => {
                setValue('termsAndConditions', !form.termsAndConditions);
              }}
            />
            {variant === 'offer' && (
              <p className="text-[1.2rem] text-brand-gray">
                You&apos;ll have 48 hours after delivery to confirm the item is &quot;as described.&quot;
              </p>
            )}
            <div className="flex justify-center gap-[1.6rem]">
              <div className="font-semibold text-brand-secondary">
                <Button
                  buttonType="button"
                  type="text"
                  text="Cancel"
                  disabled={loadingSubmit}
                  width="small"
                  onClick={() => dismiss()}
                />
              </div>
              <Button
                buttonType="submit"
                type="secondary"
                text={variant === 'offer' ? 'Submit Offer' : 'Submit Bid'}
                width="normal"
                loading={loadingSubmit}
                disabled={!product.is_flat_rate && !selectedRate}
              />
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={() => {
        if (!loadingSubmit && !offerSubmissionsInFlight.has(submissionKey)) dismiss();
      }}
      width="fit-content"
      title={
        currentStep > 0 ? (
          <div className="-ml-8 flex items-center">
            <Button
              type="text"
              leadingIcon={<ChevronLeftIcon />}
              width="small"
              onClick={() => {
                setSubmitError('');
                setCurrentStep((prev) => prev - 1);
              }}
            />

            <span className="ml-4 text-[2.4rem] font-bold">
              {steps.find((_step, index) => index === currentStep)?.title}
            </span>
          </div>
        ) : (
          <span className="ml-1 text-[2.4rem] font-bold">
            {variant === 'offer' ? 'Make An Offer' : 'Place Your Bid'}
          </span>
        )
      }
    >
      {!userDoc ? (
        <div className="relative m-auto mt-[6rem] w-[6rem]">
          <Spinner />
        </div>
      ) : isAuthExpired ? (
        <div className="flex w-full max-w-[40rem] flex-col items-center gap-6 p-8 text-center">
          <div className="text-[4rem]">⏰</div>
          <h3 className="text-[1.8rem] font-semibold">Session Expired</h3>
          <p className="text-[1.4rem] text-brand-gray">
            Your session has expired. Please refresh the page and sign in again to continue.
          </p>
          <Button
            type="primary"
            text="Refresh Page"
            onClick={() => window.location.reload()}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full max-w-[40rem]">
            <BProcess
              steps={steps}
              currentStep={currentStep}
              noStepper={true}
            />
          </div>
        </form>
      )}
    </BaseModal>
  );
};

export default MakeAnOfferModal;
